import { useMemo } from 'react';
import date from 'date-and-time';
import { useGetPromoPostsQuery } from '../../../../redux/api/fymAPI';

/** How far back a failed post still asks for attention. */
const FAILED_POSTS_LOOKBACK_DAYS = 30;

/**
 * The window runs from 30 days before today to the end of today, on the reader's local
 * midnights. A window that moved with the clock would change the query key on every render and
 * refetch in a loop. It is set once per mount; a page open across midnight keeps yesterday's
 * window, which is harmless for a 30-day badge.
 */
export function failedPostsWindow(now: Date): { from: string; to: string } {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return {
    from: date.addDays(startOfToday, -FAILED_POSTS_LOOKBACK_DAYS).toISOString(),
    to: date.addDays(startOfToday, 1).toISOString(),
  };
}

/**
 * The Calendar attention badge: posts of the last 30 days that failed to publish.
 *
 * It reads the same `GET /accounts/:id/promo/posts` the Calendar reads, so no new endpoint.
 * `undefined` while there is no account or the list has not loaded.
 */
export function useFailedPostsCount(accountId: number | null): number | undefined {
  const { from, to } = useMemo(() => failedPostsWindow(new Date()), []);
  const { data } = useGetPromoPostsQuery({ accountId: accountId ?? 0, from, to }, { skip: accountId === null });

  return useMemo(() => {
    if (data === undefined) {
      return undefined;
    }
    return data.posts.filter((post) => post.status === 'failed').length;
  }, [data]);
}
